import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// * Queries
import { useGetBuildingsByAccountQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import { useGetSubscriptionsByAccountQuery } from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import { useGetBaselineDaysByJobSubscriptionIdQuery } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';

import { useGetExtractCachedEnergySavingQuery } from '../../../api/energy-savings/extractCachedEnergySavingsApiSlice';

import IMG from './Predictive-Heat-Optimization.webp';

// * MUI
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import CardHeaderWithIcon from '@/components/card/CardHeaderWithIcon';
import {
  Alert,
  AlertTitle,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Container,
  Grid,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';

import initializeTimeRange from '@/features/building-optimization-savings-dashboard/helpers';
import { DateTime } from 'luxon';

// * Components
import GatewayLoadingIndicator from '@/components/indicators/GatewayLoadingIndicator';

const formatEnergyUnit = (value) => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000)?.toFixed(1) + ' GWh';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000)?.toFixed(1) + ' MWh';
  } else {
    return value.toFixed(0) + ' kWh';
  }
};

function formatAndEncodeTimestampForAPI(date) {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
}

const PredictiveHeatControlSavingsDashboardPage = () => {
  const account = useSelector(getSelectedAccount);
  const navigate = useNavigate();
  const { start, end, isTodayInSeason, daysUntilNextSeason } =
    initializeTimeRange();

  const { data: buildings } = useGetBuildingsByAccountQuery(
    account?.AccountId,
    {
      skip: !account?.AccountId,
    },
  );

  const { data: subscriptions } = useGetSubscriptionsByAccountQuery(
    account?.AccountId,
    {
      skip: !account?.AccountId,
    },
  );

  const predictiveSub = subscriptions?.find(
    (sub) => sub.JobType === 'PredictiveHeatControl',
  );

  const { data: baselineDays } = useGetBaselineDaysByJobSubscriptionIdQuery(
    {
      jobSubscriptionId: predictiveSub?.JobSubscriptionId,
      timestamp: {
        startDate: formatAndEncodeTimestampForAPI(new Date(start)),
        endDate: formatAndEncodeTimestampForAPI(new Date(end)),
      },
    },
    { skip: !predictiveSub?.JobSubscriptionId },
  );

  const buildingIds = useMemo(
    () => (buildings ? buildings.map((b) => b.BuildingId) : []),
    [buildings],
  );

  const {
    data: energySavings,
    isLoading: isLoadingEnergySavings,
    isFetching: isFetchingEnergySavings,
  } = useGetExtractCachedEnergySavingQuery(
    { buildingsIdArray: buildingIds },
    { skip: !account?.AccountId || buildingIds.length === 0 },
  );

  const {
    Heat,
    HeatCO2Savings,
    Gas,
    GasCO2Savings,
    Electricity,
    ElectricityCO2Savings,
    BuildingEnergySavings,
    EnergySavingsSummary,
    CO2SavingsSummary,
  } = energySavings || {};

  const buildingsEnrichedWithSavings = useMemo(() => {
    if (!buildings || !energySavings) return [];
    return buildings.map((building) => {
      const savings = BuildingEnergySavings.find(
        (s) => s.BuildingId === building.BuildingId,
      );
      return {
        ...building,
        ...savings,
      };
    });
  }, [buildings, energySavings, BuildingEnergySavings]);

  const handleBuildingClick = useCallback(
    (building) => () => {
      navigate(
        `/predictive-heat-control/${encodeURIComponent(building.Name)}`,
        {
          state: {
            buildingId: building.BuildingId,
            enrichedBuilding: building,
          },
        },
      );
    },
    [navigate],
  );

  const hasAnySupply =
    Heat?.TotalEnergyUsage != null ||
    Electricity?.TotalEnergyUsage != null ||
    Gas?.TotalEnergyUsage != null;

  // if (isTodayInSeason && baselineDays && baselineDays.length <= 1) {
  //   return (
  //     <Container maxWidth="xxl" sx={{ mt: 2, mb: 2 }}>
  //       <Stack mb={2}>
  //         <Typography variant="h5" fontWeight={600}>
  //           Energibesparelse og CO2-reduktion
  //         </Typography>
  //       </Stack>
  //       <Card variant="outlined" sx={{ mb: 2 }}>
  //         <CardMedia
  //           component="img"
  //           height="300"
  //           image={IMG}
  //           alt="green iguana"
  //           sx={{
  //             objectFit: 'cover',
  //             objectPosition: 'top',
  //           }}
  //         />
  //         <CardContent>
  //           <Typography gutterBottom variant="h5" component="div">
  //             Sæson i gang - mere data nødvendig
  //           </Typography>
  //           <Typography variant="body1">
  //             Der er kun fundet{' '}
  //             <strong>
  //               {baselineDays.length} baseline dag
  //               {baselineDays.length !== 1 ? 'e' : ''}
  //             </strong>{' '}
  //             for den nuværende varmesæson. For at sikre pålidelige og nøjagtige
  //             energibesparelsesberegninger anbefaler vi, at der er mindst{' '}
  //             <strong>10 baseline dage</strong>. Energibesparelsesdata kan være
  //             mindre stabilt og kan vise større udsving, indtil der er
  //             tilstrækkelige baseline dage til rådighed. Det forventes at der
  //             vil være tilstrækkeligt med baseline dage til rådighed omkring uge
  //             42 til at vise en løbende besparelse, fortsat med store udsving.{' '}
  //             {daysUntilNextSeason > 0 && (
  //               <>
  //                 Den næste varmesæson begynder om{' '}
  //                 <strong>{daysUntilNextSeason} dage</strong>.
  //               </>
  //             )}
  //           </Typography>
  //         </CardContent>
  //       </Card>
  //     </Container>
  //   );
  // }

  return (
    <Container maxWidth="xxl" sx={{ mt: 2, mb: 2 }}>
      <GatewayLoadingIndicator
        isLoading={isLoadingEnergySavings || isFetchingEnergySavings}
      />
      <Stack mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Energibesparelse og CO2-reduktion
        </Typography>
      </Stack>
      {isTodayInSeason && baselineDays && baselineDays.length < 10 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardMedia
            component="img"
            height="200"
            image={IMG}
            alt="green iguana"
            sx={{
              objectFit: 'cover',
              objectPosition: 'top',
            }}
          />
          <CardContent>
            <Typography gutterBottom variant="h5" component="div">
              Sæson i gang - mere data nødvendig
            </Typography>
            <Typography variant="body1">
              Der er kun fundet{' '}
              <strong>
                {baselineDays.length} baseline dag
                {baselineDays.length !== 1 ? 'e' : ''}
              </strong>{' '}
              for den nuværende varmesæson. For at sikre pålidelige og nøjagtige
              energibesparelsesberegninger anbefaler vi, at der er mindst{' '}
              <strong>10 baseline dage</strong>. Energibesparelsesdata kan være
              mindre stabilt og kan vise større udsving, indtil der er
              tilstrækkelige baseline dage til rådighed. Det forventes at der
              vil være tilstrækkeligt med baseline dage til rådighed omkring uge
              42 til at vise en løbende besparelse, fortsat med store udsving.{' '}
              {daysUntilNextSeason > 0 && (
                <>
                  Den næste varmesæson begynder om{' '}
                  <strong>{daysUntilNextSeason} dage</strong>.
                </>
              )}
            </Typography>
          </CardContent>
        </Card>
      )}
      {buildingsEnrichedWithSavings.length === 0 &&
      !isLoadingEnergySavings &&
      !isFetchingEnergySavings ? (
        <Alert
          severity="info"
          sx={{
            flexGrow: 1,
            borderLeft: (theme) => `4px solid ${theme.palette.info.main}`,
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            Ingen bygninger fundet
          </AlertTitle>
          Der er ikke fundet nogen bygninger med energibesparelser tilknyttet.
          Kontakt venligst ReMoni support for at få hjælp.
        </Alert>
      ) : (
        <>
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} md={6} lg={2.4}>
              <Card variant="outlined" sx={{ padding: 2, height: '100%' }}>
                <CardHeaderWithIcon
                  icon={<SpeedRoundedIcon />}
                  tooltip="Totalforbruget er det aflæste forbrug direkte fra forsyningsselskabet, afregningsmåleren eller hovedmåleren."
                  title="Samlet Forbrug"
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <Typography variant="h5">
                      {EnergySavingsSummary?.TotalEnergyUsage >= 1000000 ||
                      EnergySavingsSummary?.TotalEnergyUsage <= -1000000
                        ? (
                            EnergySavingsSummary?.TotalEnergyUsage / 1000000
                          ).toFixed(1) + ' GWh'
                        : EnergySavingsSummary?.TotalEnergyUsage >= 1000 ||
                            EnergySavingsSummary?.TotalEnergyUsage <= -1000
                          ? (
                              EnergySavingsSummary?.TotalEnergyUsage / 1000
                            ).toFixed(1) + ' MWh'
                          : EnergySavingsSummary?.TotalEnergyUsage.toFixed(0) +
                            ' kWh'}
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={2.4}>
              <Card variant="outlined" sx={{ padding: 2, height: '100%' }}>
                <CardHeaderWithIcon
                  icon={<EnergySavingsLeafRoundedIcon />}
                  tooltip="Den samlede besparelse er forskellen mellem det beregnede forbrug uden Prædiktiv Varmeoptimering og det aflæste forbrug i perioden. Det beregnede forbrug uden Prædiktiv Varmeoptimering for hver bygning kan findes ved at klikke ind på pågældende bygning."
                  title="Samlet energibesparelse"
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <Typography variant="h5">
                      {' '}
                      {EnergySavingsSummary?.TotalEnergySavings >= 1000000 ||
                      EnergySavingsSummary?.TotalEnergySavings <= -1000000
                        ? (
                            EnergySavingsSummary?.TotalEnergySavings / 1000000
                          ).toFixed(1) + ' GWh'
                        : EnergySavingsSummary?.TotalEnergySavings >= 1000 ||
                            EnergySavingsSummary?.TotalEnergySavings <= -1000
                          ? (
                              EnergySavingsSummary?.TotalEnergySavings / 1000
                            ).toFixed(1) + ' MWh'
                          : EnergySavingsSummary?.TotalEnergySavings.toFixed(
                              0,
                            ) + ' kWh'}
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={2.4}>
              <Card variant="outlined" sx={{ padding: 2, height: '100%' }}>
                <CardHeaderWithIcon
                  icon={<PercentRoundedIcon />}
                  tooltip="Besparelsesprocenten er baseret på forholdet mellem gennemsnitsforbruget på dage uden optimering (baselinedage) og det faktiske forbrug. Der bruges her et vægtet gennemsnit på tværs af alle bygninger tilknyttet denne konto."
                  title="Procentvis besparelse"
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <Typography variant="h5">
                      {EnergySavingsSummary?.TotalEnergySavingsPercentage.toFixed(
                        1,
                      ) + ' %' || '—'}
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={2.4}>
              <Card variant="outlined" sx={{ padding: 2, height: '100%' }}>
                <CardHeaderWithIcon
                  icon={<CloudRoundedIcon />}
                  tooltip="Besparelsen er forholdet mellem forbruget, hvis alle dage var uden optimering (baselinedage) set i forhold til det faktiske forbrug."
                  title="Samlet CO₂ besparelse"
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <Typography variant="h5">
                      {CO2SavingsSummary?.TotalCO2Savings >= 1000000 ||
                      CO2SavingsSummary?.TotalCO2Savings <= -1000000
                        ? (
                            CO2SavingsSummary?.TotalCO2Savings / 1000000
                          ).toFixed(1) + ' t CO2e'
                        : CO2SavingsSummary?.TotalCO2Savings >= 1000 ||
                            CO2SavingsSummary?.TotalCO2Savings <= -1000
                          ? (CO2SavingsSummary?.TotalCO2Savings / 1000).toFixed(
                              1,
                            ) + ' kg CO2e'
                          : CO2SavingsSummary?.TotalCO2Savings.toFixed(0) +
                            ' g CO2e'}
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} md={12} lg={2.4}>
              <Card
                variant="outlined"
                display="flex"
                flexDirection="row"
                onClick={() =>
                  navigate('/predictive-heat-control/calendar', {
                    replace: true,
                  })
                }
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light' ? '#f5f5f5' : '#303030',
                  padding: 2,
                  height: '100%',
                  cursor: 'pointer',
                  color: (theme) => theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                    color: (theme) => theme.palette.text.primary,
                    boxShadow: (theme) => theme.shadows[2],
                  },
                  transition:
                    'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.light, 0.1),
                      mr: 1,
                      borderRadius: 3,
                    }}
                  >
                    <CalendarMonthRoundedIcon
                      sx={{ verticalAlign: 'middle', color: 'primary.main' }}
                    />
                  </Avatar>
                  Kalender
                </Typography>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  Administrer begivenheder for dine bygninger.
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* <Stack spacing={2} mb={4}>
            <Alert
              severity="info"
              sx={{
                flexGrow: 1,
                borderLeft: (theme) => `4px solid ${theme.palette.info.main}`,
              }}
              variant="standard"
            >
              <AlertTitle sx={{ fontWeight: 600 }}>Information</AlertTitle>
              Den samlede besparelse på energiforbruget fra varme og den
              tilhørende CO2-reduktion for alle bygninger tilknyttet denne konto
              vises her. I kan dykke ned i dataen for den enkelte bygning, ved
              at klikke ind på den. Besparelsen bliver beregnet løbende, og
              ændres i takt med, at der kommer mere og mere data. Især i starten
              af sæsonen hvor varmeforbruget er begrænset og der er få baseline
              dage at sammenligne med, kan der være store udsving. Det
              stabiliserer sig når varmeforbruget øges i takt med at det bliver
              koldere, og der bliver flere baseline dage at sammenligne med. En
              varmesæson går fra 1. oktober til 31. maj kl. 12. Vi bruger altid
              nyeste tilgængelige målerdata.
            </Alert>
          </Stack> */}

          <Stack mb={2}>
            <Typography variant="h5" fontWeight={600} mb={1}>
              Forsyningskilder
            </Typography>
            {!Heat?.TotalEnergyUsage &&
              !Electricity?.TotalEnergyUsage &&
              !Gas?.TotalEnergyUsage &&
              !isLoadingEnergySavings &&
              !isFetchingEnergySavings && (
                <Alert
                  severity="warning"
                  sx={{
                    flexGrow: 1,
                    borderLeft: (theme) =>
                      `4px solid ${theme.palette.warning.main}`,
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                    Advarsel
                  </AlertTitle>
                  Ingen forsyningskilder er tilgængelige. Kontakt venligst
                  ReMoni.
                </Alert>
              )}
            {Heat?.TotalEnergyUsage !== 0 && (
              <ListItem
                sx={{
                  backgroundColor: 'background.paper',
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ListItemIcon>
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ borderRadius: 3 }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: alpha('#c2dd00', 0.1),
                        mr: 1,
                        borderRadius: 3,
                      }}
                    >
                      <MenuRoundedIcon
                        sx={{
                          verticalAlign: 'middle',
                          color: '#96b100',
                          transform: 'rotate(90deg)',
                        }}
                      />
                    </Avatar>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Type"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : (
                      'Fjernvarme'
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                  sx={{ flex: 2 }}
                />
                <ListItemText
                  primary="Samlet aflæst forbrug"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : Heat?.BaseUnit === 'kWh' ? (
                      formatEnergyUnit(Heat?.TotalEnergyUsage)
                    ) : (
                      `${Heat?.TotalEnergyUsage.toFixed(1)} ${Heat?.BaseUnit}`
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />

                <ListItemText
                  primary="Effektiv besparelse"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : Heat?.BaseUnit === 'kWh' ? (
                      formatEnergyUnit(Heat?.TotalEnergySavings)
                    ) : (
                      `${Heat?.TotalEnergySavings.toFixed(1)} ${Heat?.BaseUnit}`
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />

                <ListItemText
                  primary="CO2 Besparelse"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={120} height={20} />
                    ) : (
                      HeatCO2Savings?.TotalCO2Savings !== undefined &&
                      (Math.abs(HeatCO2Savings.TotalCO2Savings) >= 1000000
                        ? `${(HeatCO2Savings.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                        : Math.abs(HeatCO2Savings.TotalCO2Savings) >= 1000
                          ? `${(HeatCO2Savings.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                          : `${HeatCO2Savings.TotalCO2Savings.toFixed(0)} g CO2e`)
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />
              </ListItem>
            )}

            {Electricity?.TotalEnergyUsage !== 0 && (
              <ListItem
                sx={{
                  backgroundColor: 'background.paper',
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ListItemIcon>
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ borderRadius: 3 }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: alpha('#c2dd00', 0.1),
                        mr: 1,
                        borderRadius: 3,
                      }}
                    >
                      <BoltRoundedIcon
                        sx={{
                          verticalAlign: 'middle',
                          color: '#96b100',
                        }}
                      />
                    </Avatar>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Type"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : (
                      'El'
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                  sx={{ flex: 2 }}
                />
                <ListItemText
                  primary="Samlet aflæst forbrug"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : Electricity?.BaseUnit === 'kWh' ? (
                      formatEnergyUnit(Electricity?.TotalEnergyUsage)
                    ) : (
                      `${Electricity?.TotalEnergyUsage.toFixed(1)} ${Electricity?.BaseUnit}`
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />

                <ListItemText
                  primary="Effektiv besparelse"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : Electricity?.BaseUnit === 'kWh' ? (
                      formatEnergyUnit(Electricity?.TotalEnergySavings)
                    ) : (
                      `${Electricity?.TotalEnergySavings.toFixed(1)} ${Electricity?.BaseUnit}`
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />

                <ListItemText
                  primary="CO2 Besparelse"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : (
                      ElectricityCO2Savings?.TotalCO2Savings !== undefined &&
                      (Math.abs(ElectricityCO2Savings.TotalCO2Savings) >=
                      1000000
                        ? `${(ElectricityCO2Savings.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                        : Math.abs(ElectricityCO2Savings.TotalCO2Savings) >=
                            1000
                          ? `${(ElectricityCO2Savings.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                          : `${ElectricityCO2Savings.TotalCO2Savings.toFixed(0)} g CO2e`)
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />
              </ListItem>
            )}

            {Gas?.TotalEnergyUsage !== 0 && (
              <ListItem
                sx={{
                  backgroundColor: 'background.paper',
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ListItemIcon>
                  {isLoadingEnergySavings || isFetchingEnergySavings ? (
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ borderRadius: 3 }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: alpha('#c2dd00', 0.1),
                        mr: 1,
                        borderRadius: 3,
                      }}
                    >
                      <LocalFireDepartmentRoundedIcon
                        sx={{
                          verticalAlign: 'middle',
                          color: '#96b100',
                        }}
                      />
                    </Avatar>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Type"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : (
                      'Gas'
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                  sx={{ flex: 2 }}
                />

                <ListItemText
                  primary="Samlet aflæst forbrug"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : Gas?.BaseUnit === 'kWh' ? (
                      formatEnergyUnit(Gas?.TotalEnergyUsage)
                    ) : (
                      `${Gas?.TotalEnergyUsage.toFixed(1)} ${Gas?.BaseUnit}`
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />

                <ListItemText
                  primary="Effektiv besparelse"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : Gas?.BaseUnit === 'kWh' ? (
                      formatEnergyUnit(Gas?.TotalEnergySavings)
                    ) : (
                      `${Gas?.TotalEnergySavings.toFixed(1)} ${Gas?.BaseUnit}`
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />

                <ListItemText
                  primary="CO2 Besparelse"
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    color: 'textSecondary',
                  }}
                  secondary={
                    isLoadingEnergySavings || isFetchingEnergySavings ? (
                      <Skeleton variant="text" width={80} height={20} />
                    ) : (
                      GasCO2Savings?.TotalCO2Savings !== undefined &&
                      (Math.abs(GasCO2Savings.TotalCO2Savings) >= 1000000
                        ? `${(GasCO2Savings.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                        : Math.abs(GasCO2Savings.TotalCO2Savings) >= 1000
                          ? `${(GasCO2Savings.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                          : `${GasCO2Savings.TotalCO2Savings.toFixed(0)} g CO2e`)
                    )
                  }
                  secondaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                  }}
                />
              </ListItem>
            )}
          </Stack>

          <Stack mb={2}>
            <Typography variant="h5" fontWeight={600} mb={1}>
              Bygninger
            </Typography>

            {buildingsEnrichedWithSavings.map((building) => (
              <ListItemButton
                key={building.BuildingId}
                onClick={handleBuildingClick(building)}
                disabled={building.BillingMeters.length === 0 || !hasAnySupply}
                sx={{
                  backgroundColor: 'background.paper',
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',

                  // when hovering the whole row
                  '&:hover .arrow-icon': {
                    transform: 'rotate(180deg) translateX(-4px)',
                    color: 'primary.main',
                  },
                }}
              >
                <ListItemIcon>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: alpha('#c2dd00', 0.1),
                      mr: 1,
                      borderRadius: 3,
                    }}
                  >
                    <HomeWorkRoundedIcon
                      sx={{
                        verticalAlign: 'middle',
                        color: '#96b100',
                      }}
                    />
                  </Avatar>
                </ListItemIcon>

                <ListItemText
                  primary={building.Name}
                  primaryTypographyProps={{
                    variant: 'subtitle1',
                    color: 'textPrimary',
                    fontWeight: 500,
                  }}
                  secondary={
                    building.Location?.Address
                      ? `${building.Location.Address}`
                      : 'Ingen adresse'
                  }
                  secondaryTypographyProps={{ variant: 'subtitle2' }}
                />

                {building.BillingMeters.length === 0 && (
                  <Typography variant="body2" color="error" sx={{ mr: 2 }}>
                    Ingen målere tilgængelige
                  </Typography>
                )}

                <ListItemIcon>
                  <ArrowBackRoundedIcon
                    className="arrow-icon"
                    sx={{
                      transform: 'rotate(180deg)',
                      transition:
                        'transform 0.2s ease-in-out, color 0.2s ease-in-out',
                    }}
                  />
                </ListItemIcon>
              </ListItemButton>
            ))}
          </Stack>
        </>
      )}
    </Container>
  );
};

export default PredictiveHeatControlSavingsDashboardPage;
